import { createPrng, type Prng } from './rng.js';
import { computeReinforcements, countOwned } from './state.js';
import type {
  GameEvent,
  GameState,
  MapDefinition,
  Player,
  PlayerId,
  TerritoryId,
  TerritoryState,
  TurnContext,
  TurnPhase,
} from './types.js';

/**
 * Account-deletion forfeit. Applied server-side when a user with
 * active games triggers DELETE /api/account. Each of the
 * forfeiter's tiles is redistributed via a 4-rule fallback chain
 * (see `pickTileNewOwner`); the forfeiter is then marked eliminated;
 * turn advances if it was theirs; standard team-aware victory check
 * runs.
 *
 * The redistribution chain honors v0.7 War Room team play: tiles
 * preferentially flow to teammates so a forfeit doesn't accidentally
 * gift territory to opponents. Falls through to the original
 * mode-neighbor heuristic for FFA games and last-survivor-of-a-team
 * forfeits.
 *
 * Pure function. Does not mutate.
 *
 * Returns the unchanged state with no events if `forfeitId` is
 * already eliminated, not present in the game, or the only
 * remaining player (degenerate — let the existing victory check
 * clean up). Throws if the game is already in 'victory' status.
 */
export interface ForfeitResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

export function applyForfeit(
  state: GameState,
  map: MapDefinition,
  forfeitId: PlayerId
): ForfeitResult {
  if (state.status.kind === 'victory') {
    throw new Error('applyForfeit: game already won');
  }

  const forfeitPlayer = state.players.find((p) => p.id === forfeitId);
  if (!forfeitPlayer) {
    // Not in this game — nothing to do. Server-side caller filters by
    // membership already, but be defensive: a stale game_seats row
    // could plausibly reference a user whose state.players entry was
    // already cleaned up by an earlier action.
    return { state, events: [] };
  }
  if (forfeitPlayer.eliminated) {
    // Already out — forfeit is idempotent.
    return { state, events: [] };
  }

  // Bail early if nobody else is left to receive tiles. This shouldn't
  // happen in practice (victory check would have ended the game) but
  // we defend against it so the redistribution code can assume at
  // least one living recipient.
  const livingOthers = state.players.filter(
    (p) => p.id !== forfeitId && !p.eliminated
  );
  if (livingOthers.length === 0) {
    return { state, events: [] };
  }

  const prng = createPrng(state.rngSeed);

  // --- Redistribute tiles ---
  const territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
  };
  for (const [tileId, tile] of Object.entries(state.territories)) {
    if (tile.owner !== forfeitId) continue;
    const newOwner = pickTileNewOwner(
      state,
      map,
      forfeitPlayer,
      tileId as TerritoryId,
      prng
    );
    territories[tileId as TerritoryId] = { ...tile, owner: newOwner };
  }

  // --- Mark eliminated ---
  const players: readonly Player[] = state.players.map((p) =>
    p.id === forfeitId ? { ...p, eliminated: true } : p
  );
  const eliminationOrder = [...state.eliminationOrder, forfeitId];

  const events: GameEvent[] = [
    { kind: 'playerEliminated', playerId: forfeitId },
  ];

  // Snapshot post-redistribution + post-elimination as the working
  // state — turn advance + victory check both read from it.
  let working: GameState = {
    ...state,
    players,
    territories,
    eliminationOrder,
    rngSeed: prng.getSeed(),
  };

  // --- Advance turn if it was theirs ---
  if (state.turn.currentPlayer === forfeitId) {
    const advanced = advanceTurnFromEliminated(working, map);
    working = advanced.state;
    events.push(...advanced.events);
  }

  // --- Team-aware victory check ---
  // Mirrors checkEliminationsAndVictory in actions.ts: a single
  // surviving team wins, with the alphabetically-first living player
  // on that team as the representative winner. Skipping the
  // multi-player elimination scan because forfeit only eliminates
  // one player (the forfeiter).
  const alive = working.players.filter((p) => !p.eliminated);
  const survivingTeams = new Set(alive.map((p) => p.team));
  if (
    alive.length >= 1 &&
    survivingTeams.size === 1 &&
    working.status.kind === 'inProgress'
  ) {
    const winningTeam = alive[0]!.team;
    const representative = working.players.find(
      (p) => !p.eliminated && p.team === winningTeam
    )!;
    working = {
      ...working,
      status: {
        kind: 'victory',
        winner: representative.id,
        winningTeam,
      },
    };
    events.push({ kind: 'gameWon', winner: representative.id });
  }

  return { state: working, events };
}

// ============================================================
// Tile redistribution — the 4-rule fallback chain.
// ============================================================

/**
 * Pick the new owner of a single forfeited tile. Walks the chain:
 *
 *   1. Adjacent teammates still in game →
 *      mode-neighbor among them (ties: total tile count, then id).
 *   2. No adjacent teammates but distant teammates exist →
 *      team's leader by total tile count
 *      (ties: seeded prng).
 *   3. No teammates remain (FFA, or last-of-team forfeit) →
 *      mode-neighbor among any adjacent living non-team player
 *      (ties: total tile count, then id).
 *   4. Isolated (no living neighbors at all) →
 *      overall leader by total tile count
 *      (ties: seeded prng).
 *
 * Each rule has a single tile-count fallback for ties; rule 2 and 4
 * use prng for the final tie-break since their ranking is global
 * rather than local and the alphabetical id fallback would feel
 * arbitrary in those cases.
 *
 * Pre-conditions: at least one player other than `forfeitPlayer` is
 * still alive (caller guards). Returns a valid PlayerId for an
 * un-eliminated, non-forfeiter player.
 */
function pickTileNewOwner(
  state: GameState,
  map: MapDefinition,
  forfeitPlayer: Player,
  tileId: TerritoryId,
  prng: Prng
): PlayerId {
  const territoryDef = map.territories.find((t) => t.id === tileId);
  if (!territoryDef) {
    throw new Error(`pickTileNewOwner: territory ${tileId} not in map`);
  }

  const playersById = new Map(state.players.map((p) => [p.id, p]));

  // Living neighbors = adjacent tiles whose owner is a NON-eliminated
  // non-forfeiter player. Multiple adjacent tiles owned by the same
  // player count multiple times (that's the "mode" part — owners get
  // weighted by their adjacent-tile share).
  const livingNeighborOwners: PlayerId[] = [];
  for (const adjId of territoryDef.adjacent) {
    const adjTile = state.territories[adjId];
    if (!adjTile) continue;
    if (adjTile.owner === forfeitPlayer.id) continue;
    const owner = playersById.get(adjTile.owner);
    if (!owner || owner.eliminated) continue;
    livingNeighborOwners.push(adjTile.owner);
  }

  // RULE 1 — adjacent teammates first.
  const adjacentTeammates = livingNeighborOwners.filter(
    (id) => playersById.get(id)?.team === forfeitPlayer.team
  );
  if (adjacentTeammates.length > 0) {
    return modeOwner(adjacentTeammates, state);
  }

  // RULE 2 — distant teammates if no adjacent ones.
  const distantTeammates = state.players.filter(
    (p) =>
      p.id !== forfeitPlayer.id &&
      !p.eliminated &&
      p.team === forfeitPlayer.team
  );
  if (distantTeammates.length > 0) {
    return leaderByTileCount(distantTeammates, state.territories, prng);
  }

  // RULE 3 — mode-neighbor among any non-team living adjacent player.
  if (livingNeighborOwners.length > 0) {
    return modeOwner(livingNeighborOwners, state);
  }

  // RULE 4 — isolated tile, hand to overall leader.
  const allLiving = state.players.filter(
    (p) => p.id !== forfeitPlayer.id && !p.eliminated
  );
  return leaderByTileCount(allLiving, state.territories, prng);
}

/**
 * Pick the most-frequent owner in a neighbor list. Ties by total
 * tile count desc; second-tier tie by player id ascending
 * (deterministic, no prng consumption). Caller guarantees the list
 * is non-empty.
 */
function modeOwner(
  ownerIds: readonly PlayerId[],
  state: GameState
): PlayerId {
  const counts = new Map<PlayerId, number>();
  for (const id of ownerIds) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  let bestId: PlayerId | null = null;
  let bestCount = -1;
  let bestTotal = -1;
  for (const [id, count] of counts) {
    const total = countOwned(state.territories, id);
    if (
      count > bestCount ||
      (count === bestCount && total > bestTotal) ||
      (count === bestCount && total === bestTotal && bestId !== null && id < bestId)
    ) {
      bestId = id;
      bestCount = count;
      bestTotal = total;
    }
  }
  return bestId!;
}

/**
 * Pick the player with the most tiles. Ties broken by seeded prng
 * (caller passes a Prng so this is deterministic per game seed).
 * Caller guarantees the candidate list is non-empty.
 */
function leaderByTileCount(
  candidates: readonly Player[],
  territories: Readonly<Record<TerritoryId, TerritoryState>>,
  prng: Prng
): PlayerId {
  const ranked = candidates.map((p) => ({
    id: p.id,
    tiles: countOwned(territories, p.id),
  }));
  const maxTiles = Math.max(...ranked.map((r) => r.tiles));
  const top = ranked.filter((r) => r.tiles === maxTiles);
  if (top.length === 1) return top[0]!.id;
  return top[prng.nextInt(top.length)]!.id;
}

// ============================================================
// Turn advance after current player is eliminated.
// ============================================================

/**
 * If the forfeiter was the current player, advance to the next
 * non-eliminated player in `playerOrder`. Mirrors `applyEndTurn`'s
 * shape — fresh TurnContext, deploy phase + reinforcements computed
 * if it's turn 2+, attack phase + 0 reinforcements if it's turn 1
 * (the combat-only round). Emits `turnEnded`.
 *
 * Caller has already marked the forfeiter eliminated and passes the
 * post-elimination state in. If no living players remain (shouldn't
 * happen — caller's livingOthers check would have bailed) we return
 * the state unchanged.
 */
function advanceTurnFromEliminated(
  state: GameState,
  map: MapDefinition
): { state: GameState; events: readonly GameEvent[] } {
  const order = state.playerOrder;
  const currentIdx = order.indexOf(state.turn.currentPlayer);
  if (currentIdx < 0) {
    // Defensive — shouldn't happen.
    return { state, events: [] };
  }

  // Walk forward until we find a living player. Wrap = step crosses
  // index 0, which means the next player is "earlier" in the order
  // than the (now-eliminated) current — so turn bumps.
  let foundIdx = -1;
  let turnIncrement = 0;
  for (let step = 1; step <= order.length; step++) {
    const idx = (currentIdx + step) % order.length;
    const candidateId = order[idx]!;
    const candidate = state.players.find((p) => p.id === candidateId);
    if (!candidate || candidate.eliminated) continue;
    foundIdx = idx;
    turnIncrement = idx <= currentIdx ? 1 : 0;
    break;
  }
  if (foundIdx < 0) return { state, events: [] };

  const nextId = order[foundIdx]!;
  const turnNumber = state.turnNumber + turnIncrement;
  // Same phase semantics as applyEndTurn: turn 1 is combat-only
  // (setup deploys counted as round-1 deploys); turn 2+ resumes
  // the standard cycle.
  const phase: TurnPhase = turnNumber === 1 ? 'attack' : 'deploy';
  const reinforcements =
    phase === 'deploy' ? computeReinforcements(state, map, nextId) : 0;

  const turn: TurnContext = {
    currentPlayer: nextId,
    phase,
    attack: { kind: 'free' },
    reinforcementsRemaining: reinforcements,
    fortifyUsed: false,
    deploysThisTurn: [],
  };

  return {
    state: { ...state, turn, turnNumber },
    events: [{ kind: 'turnEnded', nextPlayer: nextId, turnNumber }],
  };
}
